import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import { injectStyle, resizeOpposite } from "../../util/injectStyle.js";
const ResizeButton = ({ initialWidth, minWidth, maxWidth, restoreWidth, onResize, onResizeFinished, onResizeOpposite, opposite, oppositeOffset, containerId })=>{
    const containerRef = useRef(null);
    const currentlyResizingRef = useRef(false);
    const collapsedDuringResizeRef = useRef(false);
    const onResizeRef = useRef(onResize);
    const onResizeFinishedRef = useRef(onResizeFinished);
    const defaultRestoreWidth = typeof initialWidth === "number" ? initialWidth : minWidth;
    const [storedRestoreWidth, setStoredRestoreWidth] = useState(restoreWidth || defaultRestoreWidth);
    onResizeRef.current = onResize;
    onResizeFinishedRef.current = onResizeFinished;
    const getContainer = useCallback((selector, singleFind = true)=>{
        const elem = document.getElementById(containerId);
        const findObj = selector ? elem?.querySelectorAll(selector) ?? null : elem;
        containerRef.current = elem;
        if (selector && singleFind) {
            return findObj && findObj[0];
        }
        return findObj;
    }, [
        containerId
    ]);
    const getContainerWidth = useCallback(()=>{
        const container = containerRef.current;
        if (!container) {
            return 0;
        }
        const computed = window.getComputedStyle(container);
        const width = parseInt(computed.width, 10);
        return width - parseInt(computed.borderLeftWidth, 10) - parseInt(computed.borderRightWidth, 10);
    }, []);
    const handleOpposite = useCallback((width)=>{
        const container = containerRef.current;
        if (!container || !opposite) {
            return false;
        }
        const customResult = onResizeOpposite?.(container.id, opposite.id, width, injectStyle);
        if (customResult !== undefined) {
            return customResult;
        }
        return resizeOpposite(container.id, opposite.id, width);
    }, [
        onResizeOpposite,
        opposite
    ]);
    const handleResize = useCallback((ev, force)=>{
        const container = containerRef.current;
        if (!container) {
            return;
        }
        const isEvent = typeof ev === "object" && "pageX" in ev;
        let x = isEvent ? ev.pageX - (container.getBoundingClientRect().left + document.body.scrollLeft) - 15 : ev;
        let move = x >= minWidth && x <= maxWidth;
        isEvent && ev.preventDefault();
        if (x < minWidth - 50) {
            move = x = 1;
        }
        const collapse = move === 1;
        collapsedDuringResizeRef.current = collapse;
        const xOpposite = x + oppositeOffset;
        window.requestAnimationFrame(()=>{
            if ((move || force) && handleOpposite(collapse ? x : xOpposite) !== false) {
                container.style.width = `${xOpposite}px`;
                !collapse && setStoredRestoreWidth(x);
                onResizeRef.current?.(x, collapse);
            }
        });
    }, [
        handleOpposite,
        maxWidth,
        minWidth,
        oppositeOffset
    ]);
    const handleResizeRef = useRef(handleResize);
    handleResizeRef.current = handleResize;
    const handleMouseUp = useCallback((ev)=>{
        document.removeEventListener("pointermove", handleResizeRef.current);
        if (currentlyResizingRef.current) {
            const width = collapsedDuringResizeRef.current ? 0 : getContainerWidth();
            onResizeFinishedRef.current?.(width);
        }
        currentlyResizingRef.current = false;
        ev.preventDefault();
    }, [
        getContainerWidth
    ]);
    const handleMouseDown = useCallback((ev)=>{
        ev.preventDefault();
        document.addEventListener("pointermove", handleResizeRef.current);
        currentlyResizingRef.current = true;
    }, []);
    const handleDoubleClick = useCallback(()=>{
        const width = getContainerWidth() > 0 ? 0 : storedRestoreWidth;
        handleResizeRef.current(width, true);
        onResizeFinishedRef.current?.(width);
    }, [
        getContainerWidth,
        storedRestoreWidth
    ]);
    useEffect(()=>{
        const resizer = getContainer(".aiot-split-resizer");
        resizer?.addEventListener("pointerdown", handleMouseDown);
        document.addEventListener("pointerup", handleMouseUp);
        return ()=>{
            resizer?.removeEventListener("pointerdown", handleMouseDown);
            document.removeEventListener("pointerup", handleMouseUp);
            document.removeEventListener("pointermove", handleResizeRef.current);
        };
    }, [
        getContainer,
        handleMouseDown,
        handleMouseUp
    ]);
    // Restore persisted width once per container — not when parent callbacks re-render.
    useEffect(()=>{
        handleResizeRef.current(defaultRestoreWidth, true);
        onResizeFinishedRef.current?.(defaultRestoreWidth);
    }, [
        containerId,
        defaultRestoreWidth
    ]);
    return /*#__PURE__*/ _jsxs("span", {
        className: "aiot-split",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "aiot-split-resizer"
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "aiot-split-collapse",
                onClick: handleDoubleClick
            })
        ]
    });
};
export { ResizeButton };
